import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { getNativeLanguageName } from '../constants/languageNames';
import { Card, Button, Alert, Modal, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import BookProgress from '../components/BookProgress';
import Message from '../components/Message';
import Loader from '../components/Loader';
import BookGuideModal from '../components/BookGuideModal';
import { getStoryContent, getJobImages, approveStory, regenerateStory, getGenaiJobDetails, retryJob } from '../actions/genaiActions';

// Statuses where story generation is in progress
const STORY_GENERATING_STATUSES = ['pending', 'story_qa'];
// Statuses where story is ready for review
const STORY_READY_STATUSES = ['story_generated', 'awaiting_approval'];
// Steps at or before the story-approval checkpoint
const STEPS_UP_TO_STORY_APPROVAL = ['A_input', 'B_generate_story', 'B1_generate_story', 'B2_translate_story', 'C_qa_story', 'E_generate_cover', 'E1_generate_cover', 'E2_translate_cover', 'D_approve_story'];

function StoryReviewScreen() {
    const { jobId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showApproveConfirm, setShowApproveConfirm] = useState(false);
    const [showGuideModal, setShowGuideModal] = useState(false);
    const [regenerationReason, setRegenerationReason] = useState('');

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    const genaiJobDetails = useSelector(state => state.genaiJobDetails);
    const { loading: loadingJob, error: errorJob, job } = genaiJobDetails;

    const genaiStory = useSelector(state => state.genaiStory);
    const { loading: loadingStory, error: errorStory, pages, summary } = genaiStory;

    const genaiImages = useSelector(state => state.genaiImages);
    const { loading: loadingImages, images } = genaiImages;

    const genaiApprove = useSelector(state => state.genaiApproveStory);
    const { loading: loadingApprove, success: successApprove, error: errorApprove } = genaiApprove;

    const genaiRegenerate = useSelector(state => state.genaiRegenerateStory);
    const { loading: loadingRegenerate, success: successRegenerate, error: errorRegenerate } = genaiRegenerate;

    const genaiRetry = useSelector(state => state.genaiRetryJob);
    const { loading: loadingRetry, success: successRetry, error: errorRetry } = genaiRetry;

    // Initial load
    useEffect(() => {
        if (!userInfo) {
            navigate('/login?redirect=/genai/create_story');
        } else {
            dispatch(getGenaiJobDetails(jobId));
            dispatch(getJobImages(jobId));
        }
    }, [dispatch, navigate, userInfo, jobId]);

    // Poll for job status updates while story is being generated or QA'd,
    // plus (for taletots-QA jobs) while waiting on staff to approve the story
    useEffect(() => {
        if (!job) return;
        const isTaletots = job.qa_mode === 'taletots';
        const waitingOnStaff = isTaletots && job.current_step === 'D_approve_story';
        if (!STORY_GENERATING_STATUSES.includes(job.status) && !waitingOnStaff) return;
        const interval = setInterval(() => {
            dispatch(getGenaiJobDetails(jobId));
        }, 5000);
        return () => clearInterval(interval);
    }, [job?.status, job?.current_step, job?.qa_mode, dispatch, jobId]);

    // taletots-QA jobs: once staff advances the workflow past the story checkpoint,
    // move the customer along automatically (they never click Approve themselves)
    useEffect(() => {
        if (job?.qa_mode === 'taletots' && job.current_step && !STEPS_UP_TO_STORY_APPROVAL.includes(job.current_step)) {
            navigate(`/genai/job/${jobId}/review-pages`);
        }
    }, [job?.qa_mode, job?.current_step, navigate, jobId]);

    // Load story content when job transitions to a "story ready" state.
    // Using job?.status as dependency avoids the stale-closure bug in setInterval.
    useEffect(() => {
        if (STORY_READY_STATUSES.includes(job?.status)) {
            dispatch(getStoryContent(jobId));
        }
    }, [job?.status, dispatch, jobId]);

    // After retry: refresh job details so polling can pick up the new pending status
    useEffect(() => {
        if (successRetry) {
            dispatch(getGenaiJobDetails(jobId));
        }
    }, [successRetry, dispatch, jobId]);

    useEffect(() => {
        if (successApprove) {
            navigate(`/genai/job/${jobId}/review-pages`);
        }
    }, [successApprove, navigate, jobId]);

    useEffect(() => {
        if (successRegenerate) {
            // Refresh job so polling sees the new story_qa status
            dispatch(getGenaiJobDetails(jobId));
            setShowRejectModal(false);
            setRegenerationReason('');
        }
    }, [successRegenerate, dispatch, jobId]);

    const handleApprove = () => {
        setShowApproveConfirm(true);
    };

    const handleConfirmApprove = () => {
        setShowApproveConfirm(false);
        dispatch(approveStory(jobId));
    };

    const handleReject = () => {
        setShowRejectModal(true);
    };

    const handleRetry = () => {
        dispatch(retryJob(jobId));
    };

    const handleSubmitRegeneration = () => {
        if (!regenerationReason.trim()) {
            alert(t('storyReview.alertNoReason'));
            return;
        }
        dispatch(regenerateStory(jobId, regenerationReason));
    };

    const getPageLabel = (pageNum) => {
        if (pageNum === 0) return t('storyReview.frontCover');
        if (pageNum === 13) return t('storyReview.backCover');
        return t('storyReview.pageN', { n: pageNum });
    };

    const getCharLabel = (i) => i === 0 ? t('storyReview.mainChar') : i === 1 ? t('storyReview.secondaryChar') : t('storyReview.characterN', { n: i + 1 });

    const buildCharacterDescriptor = (character) => {
        const parts = [
            character.type ? String(character.type).replace(/_/g, ' ') : '',
            character.subtype || '',
            character.gender || '',
            character.age || '',
        ].filter(Boolean);
        return parts.join(', ');
    };

    const isGenerating = job && STORY_GENERATING_STATUSES.includes(job.status);
    const isReady = job && STORY_READY_STATUSES.includes(job.status);
    const isFailed = job && job.status === 'failed';
    const isTaletotsQa = job?.qa_mode === 'taletots';
    const frontCover = images?.['0'];

    return (
        <div>
            <BookProgress step1 step2 jobId={jobId} />

            <h1>{t('storyReview.title')}</h1>
            <p className="text-muted">
                {t('storyReview.subtitle')}
            </p>

            {loadingJob && !job?.status ? (
                <Loader />
            ) : errorJob ? (
                <Message variant='danger'>{errorJob}</Message>
            ) : (
                <>
                    {/* Status bar */}
                    {job && (
                        isFailed ? (
                            <Alert variant="danger" className="mb-3 py-2">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <span>
                                        {t('storyReview.failed')}{' '}
                                        {job.error_message && (
                                            <span className="text-muted small">{job.error_message}</span>
                                        )}
                                    </span>
                                    <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={handleRetry}
                                        disabled={loadingRetry}
                                    >
                                        {loadingRetry ? t('storyReview.retrying') : t('storyReview.retry')}
                                    </Button>
                                </div>
                                {errorRetry && <div className="mt-2 text-danger small">{errorRetry}</div>}
                            </Alert>
                        ) : (
                            <Alert variant={isGenerating ? 'warning' : 'success'} className="mb-3 py-2">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <span>
                                        {isGenerating
                                            ? t('storyReview.generating')
                                            : t('storyReview.ready')}
                                        {' '}
                                        <strong>{t('storyReview.languageLabel')}</strong> {getNativeLanguageName(job.input_data?.language || 'English', 'English')}
                                    </span>
                                    <small className="text-muted">
                                        {t('storyReview.regensUsed', { count: job.user_regeneration_count || 0 })}
                                    </small>
                                </div>
                            </Alert>
                        )
                    )}

                    <Alert variant="info" className="mb-3">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <span>
                                <strong>Layout note:</strong> In the final printed book, story text is placed on a separate page next to each image page.
                            </span>
                            <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => setShowGuideModal(true)}
                            >
                                {t('common.viewBookLayoutGuide')}
                            </Button>
                        </div>
                    </Alert>

                    <BookGuideModal show={showGuideModal} onHide={() => setShowGuideModal(false)} />

                    {/* Character summary — hide while generating so stale data isn't shown */}
                    {!isGenerating && job?.input_data?.characters && (() => {
                        const chars = job.input_data.characters.filter(c => c.name);
                        if (!chars.length) return null;
                        return (
                            <Card className="mb-3 border-0 bg-light">
                                <Card.Body className="py-2 px-3">
                                    {(() => {
                        return chars.map((c, i) => {
                            const descriptor = buildCharacterDescriptor(c);
                            return (
                                            <div key={i} style={{ fontSize: '0.88rem', lineHeight: '1.8' }}>
                                                <span className="text-muted">{getCharLabel(i)}</span>{' '}
                                                <strong>{c.name}</strong>
                                                {descriptor && <span className="text-muted"> — {descriptor}</span>}
                                                {c.freetext && <span className="text-muted"> · {c.freetext}</span>}
                                </div>
                            );
                        });
                    })()}
                                </Card.Body>
                            </Card>
                        );
                    })()}

                    {isGenerating ? (
                        <div className="text-center py-5 text-muted">
                            <Loader />
                            <p className="mt-3">{t('storyReview.generatingMsg')}</p>
                        </div>
                    ) : loadingStory ? (
                        <Loader />
                    ) : errorStory ? (
                        <Message variant='danger'>{errorStory}</Message>
                    ) : pages && pages.length > 0 ? (
                        <>
                        {/* Story summary */}
                        {summary && (
                            <Card className="mb-4 border-0 bg-light">
                                <Card.Body>
                                    <div className="text-uppercase fw-semibold text-muted mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>{t('storyReview.summary')}</div>
                                    <p className="mb-0" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>{summary}</p>
                                </Card.Body>
                            </Card>
                        )}

                        <Card className="mb-4 border-primary">
                            <Card.Header className="bg-primary text-white">
                                <h4 className="mb-0">{t('storyReview.frontCover')}</h4>
                            </Card.Header>
                            <Card.Body className="text-center">
                                {loadingImages ? (
                                    <Loader />
                                ) : frontCover ? (
                                    <img
                                        src={frontCover.url || frontCover.image_url}
                                        alt={t('storyReview.frontCover')}
                                        className="img-fluid border rounded shadow"
                                        style={{ maxHeight: '520px' }}
                                    />
                                ) : (
                                    <Alert variant="warning" className="mb-0">
                                        {t('storyReview.frontCoverNotReady')}
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Story pages */}
                        <div className="mb-4">
                            {pages.map((page, index) => {
                                const pageNum = page.page_number ?? index;
                                const label = getPageLabel(pageNum);
                                const text = (page.text || page.text_on_page || '').replace(/\*\*/g, '');
                                const rawPic = page.picture_translated || page.picture || page.picture_description || '';
                                const cleanPic = page.picture_translated
                                    ? rawPic
                                    : rawPic.replace(/([^|]+)\|[^(]+\([^)]*\)/g, '$1');
                                return (
                                    <div key={index} className={index < pages.length - 1 ? 'mb-4 pb-4 border-bottom' : 'mb-2'}>
                                        <div className="fw-semibold mb-2" style={{ fontSize: '0.85rem' }}>{label}</div>
                                        <div className="mb-3">
                                            <div className="text-uppercase fw-semibold text-muted mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                                                {t('storyReview.pictureDesc')}
                                            </div>
                                            <p className="ps-2 border-start border-3 border-info mb-0 text-secondary" style={{ fontSize: '0.9rem' }}>
                                                {cleanPic || <em>{t('storyReview.none')}</em>}
                                            </p>
                                        </div>
                                        <div>
                                            <div className="text-uppercase fw-semibold text-muted mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                                                {t('storyReview.textOnPage')}
                                            </div>
                                            <p className="ps-2 border-start border-3 border-success mb-0" style={{ fontSize: '0.95rem' }}>
                                                {text || <em>{t('storyReview.noText')}</em>}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Review checklist */}
                        <Card className="mb-4 border-warning">
                            <Card.Body>
                                <h6 className="fw-bold mb-3">{t('storyReview.checklistTitle')}</h6>
                                <ol className="mb-0" style={{ paddingLeft: '1.2rem', lineHeight: '1.8' }}>
                                    <li><Trans i18nKey="storyReview.check1" components={{ strong: <strong /> }} /></li>
                                    <li><Trans i18nKey="storyReview.check2" components={{ strong: <strong /> }} /></li>
                                    <li><Trans i18nKey="storyReview.check3" components={{ strong: <strong /> }} /></li>
                                </ol>
                            </Card.Body>
                        </Card>
                        </>
                    ) : !isGenerating ? (
                        <Alert variant="warning">
                            {t('storyReview.noContent')}{' '}
                            {isFailed ? t('storyReview.retryHint') : t('storyReview.stillGenerating')}
                        </Alert>
                    ) : null}

                    {/* Action buttons — only shown when story is ready for review and the customer is the reviewer */}
                    {isReady && isTaletotsQa && (
                        <Alert variant="info" className="mb-4">
                            {t('storyReview.staffReviewingStory')}
                        </Alert>
                    )}
                    {isReady && !isTaletotsQa && (
                        <Card className="mb-4 p-3 bg-light">
                            {errorApprove && <Message variant='danger'>{errorApprove}</Message>}
                            {errorRegenerate && <Message variant='danger'>{errorRegenerate}</Message>}
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                    <h5 className="mb-1">{t('storyReview.approvePrompt')}</h5>
                                    <p className="mb-0 text-muted small">
                                        {t('storyReview.approveHint')}
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleReject}
                                        disabled={loadingApprove || loadingRegenerate}
                                    >
                                        {t('storyReview.requestChanges')}
                                    </Button>
                                    <Button
                                        variant="success"
                                        size="lg"
                                        onClick={handleApprove}
                                        disabled={loadingApprove || loadingRegenerate || !pages?.length}
                                    >
                                        {loadingApprove ? t('storyReview.approving') : t('storyReview.approve')}
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    )}
                </>
            )}

            {/* Feedback modal */}
            <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('storyReview.changeModalTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info">
                        <Trans i18nKey="storyReview.changeModalHint" components={{ strong: <strong /> }} />
                    </Alert>
                    <Form.Group controlId="regenerationReason">
                        <Form.Label>{t('storyReview.changeLabel')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={6}
                            placeholder={t('storyReview.changePlaceholder')}
                            value={regenerationReason}
                            onChange={(e) => setRegenerationReason(e.target.value)}
                            maxLength={1000}
                        />
                        <Form.Text className="text-muted">
                            {t('storyReview.charCount', { count: regenerationReason.length, max: 1000 })}
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
                        {t('storyReview.cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmitRegeneration}
                        disabled={loadingRegenerate || !regenerationReason.trim()}
                    >
                        {loadingRegenerate ? t('storyReview.regenerating') : t('storyReview.regenerate')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Approve confirmation modal */}
            <Modal show={showApproveConfirm} onHide={() => setShowApproveConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('storyReview.illustrateTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('storyReview.illustrateBody')}</p>
                    <p className="text-muted mb-0">{t('storyReview.illustrateNote')}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowApproveConfirm(false)}>
                        {t('storyReview.goBack')}
                    </Button>
                    <Button variant="success" onClick={handleConfirmApprove}>
                        {t('storyReview.illustrateConfirm')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default StoryReviewScreen;
